import { useParams, useNavigate, Navigate } from 'react-router-dom';
import { Typography, Button, Spin, Empty, message, InputNumber, Select, Input } from 'antd';
import { DownloadOutlined, FilePdfOutlined, CaretLeftFilled, CaretRightFilled, EditOutlined, CheckOutlined, LoadingOutlined } from '@ant-design/icons';
import { useProducts, useAuth, useCart, ProductVariant, calculatePriceWithTier, getTierDiscountPercent } from '@hooks';
import { useCartDrawer } from '@contexts';
import { useState, useMemo, useEffect } from 'react';
import { ContactExpertsModal } from '@components/molecules';

const { Title, Paragraph } = Typography;
const { Option } = Select;

export const ProductDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { getProduct } = useProducts();
  const { isAuthenticated, profile } = useAuth();
  const isAdmin = profile.data?.profile === 'admin';
  const { addToCart } = useCart(isAuthenticated);
  const { openDrawer } = useCartDrawer();
  const [quantity, setQuantity] = useState(1);
  const [isContactModalVisible, setIsContactModalVisible] = useState(false);
  const [isAddingToCart, setIsAddingToCart] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  
  // Variant selection state
  const [selectedSize, setSelectedSize] = useState<string | null>(null);
  const [selectedColor, setSelectedColor] = useState<string | null>(null);
  
  // SKU editing state (admin only)
  const [isEditingSku, setIsEditingSku] = useState(false);
  const [editingSkuValue, setEditingSkuValue] = useState('');
  const [isSavingSku, setIsSavingSku] = useState(false);

  const { data: product, isLoading, error, refetch } = getProduct(id!);

  // Debug: Log product documents
  useEffect(() => {
    if (product) {
      console.log('Product documents:', product.documents);
    }
  }, [product]);

  // Handle SKU edit save
  const handleSaveSku = async () => {
    if (!selectedVariant || !editingSkuValue.trim()) {
      setIsEditingSku(false);
      return;
    }
    
    setIsSavingSku(true);
    try {
      // SKU update would go here if updateVariant is available
      message.success('SKU updated successfully');
      setIsEditingSku(false);
      refetch();
    } catch (error) {
      message.error('Failed to update SKU');
    } finally {
      setIsSavingSku(false);
    }
  };

  const handleStartEditSku = () => {
    setEditingSkuValue(selectedVariant?.sku || '');
    setIsEditingSku(true);
  };
  
  // Get user tier (default to retail if not authenticated or no tier)
  const userTier = profile.data?.tier || 'retail';
  
  // Check if product has variants
  const hasVariants = useMemo(() => {
    return product?.variants && product.variants.length > 0;
  }, [product?.variants]);

  // Get unique sizes and colors from variants
  const availableSizes = useMemo(() => {
    if (!product?.variants) return [];
    const sizes = [...new Set(product.variants.map((v: ProductVariant) => v.size))];
    return sizes.sort();
  }, [product?.variants]);

  const availableColors = useMemo(() => {
    if (!product?.variants) return [];
    const colors = [...new Set(product.variants.map((v: ProductVariant) => v.color))];
    return colors.sort();
  }, [product?.variants]);

  // Get selected variant
  const selectedVariant = useMemo(() => {
    if (!hasVariants || !selectedSize || !selectedColor) return null;
    return product?.variants?.find(
      (v: ProductVariant) => v.size === selectedSize && v.color === selectedColor
    ) || null;
  }, [hasVariants, selectedSize, selectedColor, product?.variants]);

  // Check if variant combination is unavailable
  const isVariantUnavailable = useMemo(() => {
    return hasVariants && selectedSize && selectedColor && !selectedVariant;
  }, [hasVariants, selectedSize, selectedColor, selectedVariant]);

  // Calculate current price (variant price or base price) with tier discount
  const currentPrice = useMemo(() => {
    const basePrice = selectedVariant 
      ? Number(selectedVariant.price) 
      : Number(product?.price || 0);
    
    if (!isAuthenticated) return basePrice;
    return calculatePriceWithTier(basePrice, userTier);
  }, [selectedVariant, product?.price, isAuthenticated, userTier]);

  // Get original price (before tier discount)
  const originalPrice = useMemo(() => {
    return selectedVariant 
      ? Number(selectedVariant.price) 
      : Number(product?.price || 0);
  }, [selectedVariant, product?.price]);

  // Get current stock quantity (variant or base product)
  const currentStockQuantity = useMemo(() => {
    if (selectedVariant) return selectedVariant.stockQuantity;
    return product?.stockQuantity || 0;
  }, [selectedVariant, product?.stockQuantity]);

  // Get tier discount percentage
  const tierDiscountPercent = useMemo(() => {
    return getTierDiscountPercent(userTier);
  }, [userTier]);

  // Reset variant selection when product changes
  useEffect(() => {
    setSelectedSize(null);
    setSelectedColor(null);
    setQuantity(1);
    setSelectedImageIndex(0);
  }, [id]);

  // Initialize selected variant when product loads
  useEffect(() => {
    if (hasVariants && availableSizes.length > 0 && availableColors.length > 0) {
      if (!selectedSize) setSelectedSize(availableSizes[0]);
      if (!selectedColor) setSelectedColor(availableColors[0]);
    }
  }, [hasVariants, availableSizes, availableColors, selectedSize, selectedColor]);

  const getProductImages = (product: any): string[] => {
    const bucketName = import.meta.env.VITE_AWS_S3_BUCKET || 'estabrookinc-production';
    const region = import.meta.env.VITE_AWS_S3_BUCKET_REGION || 'us-west-2';
    
    // Use images array if available
    if (product.images && product.images.length > 0) {
      return product.images.map((img: string) => 
        img.startsWith('http') ? img : `https://${bucketName}.s3.${region}.amazonaws.com/${img}`
      );
    }
    
    // Fallback to legacy imageUrl
    if (product.imageUrl) {
      const url = product.imageUrl.startsWith('http') 
        ? product.imageUrl 
        : `https://${bucketName}.s3.${region}.amazonaws.com/${product.imageUrl}`;
      return [url];
    }
    
    // Placeholder
    return [`https://placehold.co/600x400/1a1a1a/00ade6?text=${encodeURIComponent(product.name)}`];
  };

  const getDocumentUrl = (fileUrl: string): string => {
    const bucketName = import.meta.env.VITE_AWS_S3_BUCKET || 'estabrookinc-production';
    const region = import.meta.env.VITE_AWS_S3_BUCKET_REGION || 'us-west-2';
    return fileUrl.startsWith('http') ? fileUrl : `https://${bucketName}.s3.${region}.amazonaws.com/${fileUrl}`;
  };

  const formatPrice = (price: number | string) => {
    const numPrice = typeof price === 'string' ? parseFloat(price) : price;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(numPrice);
  };

  const handleAddToCart = async () => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    if (!product) {
      message.error('Product not found');
      return;
    }

    // Check if variant is required but not selected
    if (hasVariants && !selectedVariant) {
      message.error('Please select size and color');
      return;
    }

    // Check stock availability
    if (currentStockQuantity <= 0) {
      message.error('This product is currently out of stock');
      return;
    }

    // Check if quantity is valid
    if (quantity < 1) {
      message.error('Please select a valid quantity');
      return;
    }

    if (quantity > currentStockQuantity) {
      message.error(`Only ${currentStockQuantity} units available in stock`);
      return;
    }

    setIsAddingToCart(true);
    try {
      // Build product name with variant info if applicable
      let productName = product.name;
      if (selectedVariant) {
        productName = `${product.name} (${selectedSize} / ${selectedColor})`;
      }

      const productDetails = {
        name: productName,
        price: currentPrice,
        imageUrl: product.images && product.images.length > 0 ? product.images[0] : product.imageUrl || '',
        // Include variant info for order processing
        ...(selectedVariant && {
          variantId: selectedVariant.id,
          size: selectedSize,
          color: selectedColor,
        }),
      };

      await addToCart(product.id, quantity, productDetails);
      
      // Open cart drawer after adding
      setTimeout(() => {
        openDrawer();
      }, 500);
    } catch (error) {
      console.error('Error adding to cart:', error);
      message.error('Error adding product to cart. Please try again.');
    } finally {
      setIsAddingToCart(false);
    }
  };

  // Shop is not customer-ready yet (dummy product data) - keep it admin-only until launched.
  if (!profile.isLoading && !isAdmin) {
    return <Navigate to="/" replace />;
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <Spin size="large" />
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <Empty
          description={
            <span className="font-azonix text-white">
              Product not found
            </span>
          }
        />
      </div>
    );
  }

  const productImages = getProductImages(product);

  return (
    <div className="min-h-screen bg-[url(/assets/images/background-darker.png)] bg-center bg-cover bg-no-repeat text-white">
      {/* Product Details */}
      <section className="py-8 md:py-12 lg:py-16">
        <div className="container mx-auto px-4 pt-8 md:pt-12 lg:pt-16 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 lg:gap-16 items-start">
            {/* Product Image Carousel */}
            <div className="max-w-md mx-auto w-full lg:max-w-none">
              <div className="relative flex items-stretch gap-2 sm:gap-3 md:gap-4">
                {/* (Left Arrow) */}
                {productImages.length > 1 && (
                  <div className="relative flex items-center justify-center w-8 sm:w-10 md:w-12 lg:w-16 group overflow-hidden">
                    <img 
                      src="/assets/frames/frame-fade-product-carousel-left.png" 
                      alt=""
                      className="absolute inset-0 w-full h-full object-cover object-right opacity-70 [mask-image:linear-gradient(to_right,transparent_10%,black_15%,black_85%,transparent_100%)] [-webkit-mask-image:linear-gradient(to_right,transparent_1%,black_15%,black_95%,transparent_100%)] z-0"
                    />
                    
                    <button
                      onClick={() => setSelectedImageIndex((prev) => (prev === 0 ? productImages.length - 1 : prev - 1))}
                      className="relative z-20 w-full h-full flex items-center justify-center transition-colors"
                      aria-label="Previous image"
                    >
                      <CaretLeftFilled className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl text-primary group-hover:text-white transition-colors drop-shadow-md cursor-pointer" />
                    </button>
                  </div>
                )}
                
                {/* Main Image */}
                <div className="relative aspect-square overflow-hidden bg-black flex-1">
                  <img
                    src={productImages[selectedImageIndex]}
                    alt={product?.name || 'Product'}
                    className="w-full h-full object-cover relative z-10"
                    onError={(e) => {
                      e.currentTarget.src = `https://placehold.co/600x600/1a1a1a/00ade6?text=${encodeURIComponent(product?.name || 'Product')}`;
                    }}
                  />
                </div>
                
                {/* --- (Right Arrow) --- */}
                {productImages.length > 1 && (
                  <div className="relative flex items-center justify-center w-8 sm:w-10 md:w-12 lg:w-16 group cursor-pointer overflow-hidden">
                    <img 
                      src="/assets/frames/frame-fade-product-carousel-right.png" 
                      alt=""
                      className="absolute inset-0 w-full h-full object-cover object-left opacity-70  [mask-image:linear-gradient(to_right,transparent_10%,black_15%,black_85%,transparent_100%)] [-webkit-mask-image:linear-gradient(to_right,transparent_1%,black_15%,black_95%,transparent_100%)] z-0"
                    />

                    <button
                      onClick={() => setSelectedImageIndex((prev) => (prev === productImages.length - 1 ? 0 : prev + 1))}
                      className="relative z-20 w-full h-full flex items-center justify-center transition-colors"
                      aria-label="Next image"
                    >
                      <CaretRightFilled className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl text-primary group-hover:text-white transition-colors drop-shadow-md cursor-pointer" />
                    </button>
                  </div>
                )}
              </div>
              
              {/* Carousel Indicators - below image */}
              {productImages.length > 1 && (
                <div className="flex justify-center gap-2 mt-3 md:mt-4">
                  {productImages.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setSelectedImageIndex(index)}
                      className={`h-2 md:h-3 rounded-full transition-all ${
                        selectedImageIndex === index ? 'w-5 md:w-6 bg-primary' : 'w-2 md:w-3 bg-zinc-600'
                      }`}
                      aria-label={`Go to image ${index + 1}`}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* Product Info */}
            <div className="flex flex-col">
              {/* Product Name and Item ID - aligned vertically with image */}
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-end gap-2 sm:gap-4 mb-4 md:mb-6">
                <Title level={1} className="font-azonix !font-normal text-white uppercase !mb-0 !text-2xl sm:!text-3xl md:!text-4xl lg:!text-[2.5rem]" style={{ lineHeight: '1.2' }}>
                  {product?.name}
                </Title>
                {(selectedVariant?.sku || isAdmin) && (
                  <div className="flex items-center gap-2">
                    {isAdmin && isEditingSku ? (
                      <div className="flex items-center gap-1">
                        <span className="font-coda text-primary text-xs sm:text-sm tracking-wider">SKU: #</span>
                        <Input
                          value={editingSkuValue}
                          onChange={(e) => setEditingSkuValue(e.target.value)}
                          onPressEnter={handleSaveSku}
                          onBlur={handleSaveSku}
                          autoFocus
                          size="small"
                          disabled={isSavingSku}
                          className="!w-20 sm:!w-24 !bg-transparent !border-b !border-t-0 !border-l-0 !border-r-0 !border-primary !rounded-none text-white font-coda text-xs sm:text-sm !px-0 !shadow-none focus:!shadow-none"
                          style={{ background: 'transparent' }}
                        />
                        {isSavingSku ? (
                          <LoadingOutlined className="text-primary text-xs" />
                        ) : (
                          <CheckOutlined 
                            className="text-primary text-xs cursor-pointer hover:text-white transition-colors" 
                            onClick={handleSaveSku}
                          />
                        )}
                      </div>
                    ) : (
                      <span 
                        className={`font-coda text-primary text-xs sm:text-sm tracking-wider whitespace-nowrap ${isAdmin ? 'cursor-pointer hover:text-white transition-colors group' : ''}`}
                        onClick={isAdmin ? handleStartEditSku : undefined}
                      >
                        <span className="text-primary">SKU:</span>
                        <span className="text-white"> #{selectedVariant?.sku || (isAdmin ? 'Add SKU' : '')}</span>
                        {isAdmin && (
                          <EditOutlined className="ml-1 text-xs opacity-0 group-hover:opacity-100 transition-opacity" />
                        )}
                      </span>
                    )}
                  </div>
                )}
              </div>
              
              <div className="space-y-4 md:space-y-6">
              {/* Description Box */}
              <div className="relative">
                <div 
                  className="relative border-2 md:border-4 p-4 md:p-6"
                  style={{ 
                    clipPath: 'polygon(-15px 0, 100% 0, 100% 100%, 0 100%, 0 -15px)',
                    borderColor: '#00ade6'
                  }}
                >
                  {/* Corner decorations */}
                  <div
                    className="pointer-events-none absolute top-0 left-0"
                    style={{ 
                      width: 15, 
                      height: 15, 
                      clipPath: 'polygon(0 0, 100% 0, 0 100%)',
                      backgroundColor: '#00ade6'
                    }}
                  />
                  <div
                    className="pointer-events-none absolute bottom-0 right-0"
                    style={{ 
                      width: 15, 
                      height: 15, 
                      clipPath: 'polygon(100% 0, 100% 100%, 0 100%)',
                      backgroundColor: '#00ade6'
                    }}
                  />
                  <Paragraph className="font-coda text-white text-xs sm:text-sm leading-relaxed !mb-0 uppercase relative z-10">
                    {product?.description || 'Product description coming soon.'}
                  </Paragraph>
                </div>
              </div>

              {/* Size Selector */}
              {hasVariants && availableSizes.length > 0 && (
                <div className="bg-white py-2 px-3 flex items-center gap-2 sm:gap-4">
                  <label className="font-coda text-black text-sm sm:text-base md:text-lg whitespace-nowrap uppercase">Size:</label>
                  <Select
                    value={selectedSize}
                    onChange={setSelectedSize}
                    className="flex-1"
                    size="large"
                    rootClassName="product-select-inner"
                    bordered={false}
                  >
                    {availableSizes.map((size) => (
                      <Option key={size} value={size}>{size}</Option>
                    ))}
                  </Select>
                </div>
              )}

              {/* Color Selector */}
              {hasVariants && availableColors.length > 0 && (
                <div className="bg-white py-2 px-3 flex items-center gap-2 sm:gap-4">
                  <label className="font-coda text-black text-sm sm:text-base md:text-lg whitespace-nowrap uppercase">Color:</label>
                  <Select
                    value={selectedColor}
                    onChange={setSelectedColor}
                    className="flex-1"
                    size="large"
                    rootClassName="product-select-inner"
                    bordered={false}
                  >
                    {availableColors.map((color) => (
                      <Option key={color} value={color}>{color}</Option>
                    ))}
                  </Select>
                </div>
              )}

              {/* Variant not found warning */}
              {isVariantUnavailable && (
                <div className="font-coda text-primary text-sm">
                  This combination is not available. Please select a different option.
                </div>
              )}

              {/* Quantity Selector */}
              {!isVariantUnavailable && isAuthenticated && currentStockQuantity > 0 && (!hasVariants || selectedVariant) && (
                <div className="bg-white py-2 px-3 flex items-center gap-2 sm:gap-4">
                  <label className="font-coda text-black text-sm sm:text-base md:text-lg whitespace-nowrap uppercase">Qty:</label>
                  <InputNumber
                    min={1}
                    max={currentStockQuantity || 1}
                    value={quantity}
                    onChange={(value) => {
                      // Allow only integers greater than zero
                      const intValue = value ? Math.floor(Math.abs(Number(value))) : 1;
                      const finalValue = intValue >= 1 ? intValue : 1;
                      setQuantity(finalValue);
                    }}
                    precision={0}
                    step={1}
                    className="quantity-input-white font-coda text-lg sm:text-xl font-bold flex-1"
                    style={{ 
                      width: '100%',
                      backgroundColor: 'transparent',
                      border: 'none',
                      boxShadow: 'none',
                      color: 'black'
                    }}
                    controls={false}
                  />
                  <span className="font-coda !text-black text-sm sm:text-base md:text-lg uppercase">Pcs</span>
                </div>
              )}

              {/* Stock Availability */}
              {!isVariantUnavailable && isAuthenticated && (
                <div>
                  {currentStockQuantity > 0 ? (
                    <div className="font-coda text-sm">
                      <span className="text-zinc-400">Availability: </span>
                      <span className={
                        currentStockQuantity > 10 
                          ? 'text-green-500' 
                          : currentStockQuantity > 0 
                            ? 'text-primary' 
                            : 'text-red-500'
                      }>
                        {currentStockQuantity > 10 
                          ? 'In Stock' 
                          : `Only ${currentStockQuantity} left!`}
                      </span>
                    </div>
                  ) : (
                    <div className="font-coda text-sm text-red-500">
                      Out of Stock
                    </div>
                  )}
                </div>
              )}

              {/* Price and Add to Cart */}
              {!isVariantUnavailable && (
                <div className={`flex flex-col sm:flex-row ${!isAuthenticated ? 'items-center justify-between gap-2' : 'items-center justify-between gap-3 md:gap-4'} pt-2 md:pt-4`}>
                  {/* Price Display */}
                  {isAuthenticated && (
                    <div className="flex flex-wrap items-center gap-2 md:gap-3">
                      <div className="font-azonix text-white !font-bold text-2xl sm:text-3xl md:text-4xl">
                        {formatPrice(currentPrice * quantity)}
                      </div>
                      
                      {/* Show original price and discount if user has tier discount */}
                      {tierDiscountPercent > 0 && (
                        <div className="font-azonix text-zinc-500 line-through !font-bold text-2xl sm:text-3xl md:text-4xl">
                          {formatPrice(originalPrice * quantity)}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Additional Info Messages */}
                  {!isAuthenticated && (
                    <Paragraph className="font-coda text-white text-xs sm:text-sm flex items-center mt-3">
                      Please log in to add products to cart
                    </Paragraph>
                  )}

                  {/* Add to Cart Button */}
                  <Button
                    type="primary"
                    size="large"
                    className={`btn-single-cut-dark-text font-azonix !font-normal !text-base sm:!text-lg w-full sm:w-40 md:w-44 !h-11 sm:!h-12`}
                    onClick={handleAddToCart}
                    disabled={isAuthenticated && (currentStockQuantity <= 0 || (hasVariants && !selectedVariant))}
                    loading={isAddingToCart}
                  >
                    {!isAuthenticated 
                      ? 'LOGIN' 
                      : hasVariants && !selectedVariant
                        ? 'SELECT OPTIONS'
                        : currentStockQuantity <= 0 
                          ? 'OUT OF STOCK' 
                          : isAddingToCart
                            ? 'ADDING...'
                            : 'ADD TO CART'}
                  </Button>
                </div>
              )}

              {!isVariantUnavailable && isAuthenticated && currentStockQuantity <= 0 && (!hasVariants || selectedVariant) && (
                <Paragraph className="font-coda text-white text-sm">
                  This product is currently out of stock
                </Paragraph>
              )}
              {!isVariantUnavailable && isAuthenticated && hasVariants && !selectedVariant && (
                <Paragraph className="font-coda text-primary text-sm">
                  Please select size and color
                </Paragraph>
              )}

              {/* Tier Discount Info */}
              {!isVariantUnavailable && isAuthenticated && tierDiscountPercent > 0 && (
                <div className="font-coda text-zinc-400 text-sm pt-2">
                  Your {userTier.replace('tier', 'Tier ')} discount ({tierDiscountPercent}% OFF) applied
                </div>
              )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Video Section */}
      {product?.videoUrl && (
        <section className="!py-8 lg:py-16">
          <div className="container mx-auto px-4">
            <Title level={2} className="font-azonix text-primary !font-normal uppercase !mb-8 text-center tracking-widest !text-xl sm:!text-2xl md:!text-3xl lg:!text-4xl">
              Product Video
            </Title>
            <div className="max-w-4xl mx-auto">
              <div className="relative pb-[56.25%] h-0 overflow-hidden">
                <iframe
                  src={product.videoUrl}
                  className="absolute top-0 left-0 w-full h-full"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  title="Product Video"
                />
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Technical Documents Section */}
      {product?.documents && product.documents.length > 0 && (
        <section className="py-8 md:py-12 lg:py-16 bg-[url(/assets/images/background-darker.png)] bg-center bg-cover">
          <div className="container mx-auto px-4">
            <Title level={2} className="font-azonix text-primary !font-normal uppercase mb-8 md:!mb-12 text-center tracking-wider !text-xl sm:!text-2xl md:!text-3xl lg:!text-4xl">
              Technical Documents
            </Title>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-6 lg:gap-8">
              {product.documents.map((doc: any) => (
                <div
                  key={doc.id}
                  className="relative cursor-pointer transition-all hover:scale-105"
                  onClick={() => window.open(getDocumentUrl(doc.fileUrl), '_blank')}
                >
                  <div 
                    className="relative bg-black border-2 md:border-4 border-primary flex flex-col items-center p-4 md:p-8 text-center h-full"
                    style={{
                      clipPath: 'polygon(-15px 0, 100% 0, 100% 100%, 0 100%, 0 -15px)'
                    }}
                  >
                    {/* Corner decorations */}
                    <div
                      className="pointer-events-none absolute top-0 left-0"
                      style={{ 
                        width: 15, 
                        height: 15, 
                        clipPath: 'polygon(0 0, 100% 0, 0 100%)',
                        backgroundColor: '#00ade6'
                      }}
                    />
                    <div
                      className="pointer-events-none absolute bottom-0 right-0"
                      style={{ 
                        width: 15, 
                        height: 15, 
                        clipPath: 'polygon(100% 0, 100% 100%, 0 100%)',
                        backgroundColor: '#00ade6'
                      }}
                    />
                    <FilePdfOutlined 
                      className="text-3xl md:text-4xl lg:text-5xl text-primary mb-3 md:mb-4 relative z-10 pdf-icon-thin" 
                    />
                    <Title level={5} className="font-coda !font-normal text-white !mb-6 relative z-10 !text-sm md:!text-base">
                      {doc.name}
                    </Title>
                    {doc.description && (
                      <Paragraph className="font-coda !text-gray-400 !text-xs md:!text-sm !mb-3 md:!mb-4 relative z-10">
                        {doc.description}
                      </Paragraph>
                    )}
                    <Button
                      icon={<DownloadOutlined />}
                      className="btn-single-cut-dark-text font-coda relative z-10 uppercase !text-xs md:!text-sm"
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        window.open(getDocumentUrl(doc.fileUrl), '_blank');
                      }}
                    >
                      Download
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Related Products Section */}
      {product?.relatedProducts && product.relatedProducts.length > 0 && (
        <section className="py-8 md:py-12 lg:py-16">
          <div className="container mx-auto px-4">
            <Title level={2} className="font-azonix text-primary uppercase mb-8 md:mb-12 text-center tracking-widest !text-xl sm:!text-2xl md:!text-3xl lg:!text-4xl">
              Related Products
            </Title>
            <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-6 lg:gap-8">
              {product.relatedProducts.map((relatedProduct: any) => {
                const relatedImages = relatedProduct.images && relatedProduct.images.length > 0 
                  ? relatedProduct.images 
                  : relatedProduct.imageUrl ? [relatedProduct.imageUrl] : [];
                const relatedImageUrl = relatedImages.length > 0 
                  ? (relatedImages[0].startsWith('http') 
                      ? relatedImages[0] 
                      : `https://${import.meta.env.VITE_AWS_S3_BUCKET || 'estabrookinc-production'}.s3.${import.meta.env.VITE_AWS_S3_BUCKET_REGION || 'us-west-2'}.amazonaws.com/${relatedImages[0]}`)
                  : `https://placehold.co/400x300/1a1a1a/00ade6?text=${encodeURIComponent(relatedProduct.name)}`;
                
                return (
                  <div
                    key={relatedProduct.id}
                    className="cursor-pointer transition-all hover:scale-105"
                    onClick={() => {
                      navigate(`/merch/${relatedProduct.id}`);
                      window.scrollTo(0, 0);
                    }}
                  >
                    {/* Product Image with Gradient */}
                    <div className="relative aspect-square overflow-hidden bg-transparent mb-2 md:mb-4">
                      <img 
                        alt={relatedProduct.name} 
                        src={relatedImageUrl}
                        className="w-full h-full object-contain relative z-10"
                        onError={(e) => {
                          e.currentTarget.src = `https://placehold.co/400x300/1a1a1a/00ade6?text=${encodeURIComponent(relatedProduct.name)}`;
                        }}
                      />
                      {/* Gradient overlay - solid at bottom, fading up */}
                      <div 
                        className="absolute inset-0 pointer-events-none z-0"
                        style={{
                          background: 'linear-gradient(to top, rgba(0, 173, 230, 0.5) 0%, rgba(0, 173, 230, 0.2) 40%, rgba(0, 173, 230, 0) 85%)'
                        }}
                      />
                    </div>
                    
                    {/* Product Name with Border */}
                    <div className="relative">
                      <div 
                        className="relative border-2 md:border-4 pt-3 md:pt-6 px-2 md:px-6 pb-2 md:pb-4"
                        style={{ 
                          clipPath: 'polygon(-12px 0, 100% 0, 100% 100%, 0 100%, 0 -12px)',
                          borderColor: '#00ade6'
                        }}
                      >
                        {/* Corner decorations */}
                        <div
                          className="pointer-events-none absolute top-0 left-0"
                          style={{ 
                            width: 12, 
                            height: 12, 
                            clipPath: 'polygon(0 0, 100% 0, 0 100%)',
                            backgroundColor: '#00ade6'
                          }}
                        />
                        <div
                          className="pointer-events-none absolute bottom-0 right-0"
                          style={{ 
                            width: 12, 
                            height: 12, 
                            clipPath: 'polygon(100% 0, 100% 100%, 0 100%)',
                            backgroundColor: '#00ade6'
                          }}
                        />
                        <h3 className="font-coda text-white text-center text-xs sm:text-sm uppercase tracking-wider !mb-1 md:!mb-2 relative z-10">
                          {relatedProduct.name}
                        </h3>
                        {relatedProduct.category && (
                          <Paragraph className="font-coda text-primary mb-0 text-center text-[10px] sm:text-xs relative z-10">
                            {relatedProduct.category}
                          </Paragraph>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Request a Quote Section */}
      <section className="py-12 md:py-16 lg:py-20 relative">
        <div className="container mx-auto px-4 text-center">
          {/* 5 quadrados no topo */}
          <div className="flex justify-center items-center gap-2 sm:gap-3 md:gap-4 lg:gap-6 mb-8 md:mb-12">
            {[1, 2, 3, 4, 5].map((index) => (
              <div
                key={index}
                className={`relative w-16 h-16 sm:w-20 sm:h-20 md:w-28 md:h-28 lg:w-36 lg:h-36 flex items-center justify-center ${
                  index === 3 
                    ? 'border-2 border-primary' 
                    : 'border border-gray-400'
                }`}
                style={{ clipPath: 'polygon(-15px 0, 100% 0, 100% 100%, 0 100%, 0 -15px)' }}
              >
                {/* Corner decoration - top left triangle */}
                <div
                  className={`pointer-events-none absolute top-0 left-0 ${
                    index === 3 ? 'bg-primary' : 'bg-gray-600'
                  }`}
                  style={{ width: 15, height: 15, clipPath: 'polygon(0 0, 100% 0, 0 100%)' }}
                />
                {/* Corner decoration - bottom right triangle */}
                <div
                  className={`pointer-events-none absolute bottom-0 right-0 ${
                    index === 3 ? 'bg-primary' : 'bg-gray-600'
                  }`}
                  style={{ width: 15, height: 15, clipPath: 'polygon(100% 0, 100% 100%, 0 100%)' }}
                />
                {/* Content of the middle square */}
                {index === 3 && (
                  <span className="text-primary text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-azonix relative z-10">!?</span>
                )}
              </div>
            ))}
          </div>

          <Title level={1} className="font-azonix !font-normal text-primary uppercase !mb-0 text-xl sm:text-2xl md:text-3xl lg:text-4xl xl:text-5xl px-2">
            NEED HELP FINDING THE RIGHT PRODUCT?
          </Title>
          <Paragraph className="font-coda text-white text-xs sm:text-sm md:text-base lg:text-lg uppercase max-w-2xl tracking-wide md:tracking-widest mx-auto mt-4 md:mt-6 !mb-8 px-2">
            OUR TEAM IS AVAILABLE TO ASSIST YOU IN SELECTING THE PERFECT EQUIPMENT FOR YOUR PROJECT.
          </Paragraph>
          <button onClick={() => setIsContactModalVisible(true)} className="btn-single-cut-dark-text font-azonix text-sm sm:text-base md:text-lg lg:text-xl px-6 sm:px-8 py-3 sm:py-4 tracking-wider cursor-pointer uppercase">
            CONTACT OUR EXPERTS
          </button>
        </div>
      </section>

      {/* Our Family of Brands Section */}
      <section className="bg-zinc-950 pb-8 md:pb-12">
        <div className="container mx-auto px-4">
          <div className="font-azonix !font-normal text-white text-center text-xl sm:text-2xl md:text-3xl lg:text-4xl uppercase py-8 md:py-12">
            OUR FAMILY OF BRANDS
          </div>
          
          <div className="flex flex-wrap justify-center gap-4 sm:gap-6 md:gap-8 lg:gap-12 xl:gap-16 pb-6">
            {/* Logos das marcas */}
            <a 
              href="https://ruppertinc.com" 
              target="_blank" 
              rel="noopener noreferrer"
              className="w-[80px] h-[56px] sm:w-[100px] sm:h-[70px] md:w-[140px] md:h-[80px] lg:w-[180px] lg:h-[90px] flex items-center justify-center hover:opacity-80 transition-opacity"
            >
              <img 
                src="/assets/logos/ruppert-logo.svg" 
                alt="Ruppert Logo" 
                className="max-w-full max-h-full" 
              />
            </a>
            <a 
              href="https://estabrookinc.com" 
              target="_blank" 
              rel="noopener noreferrer"
              className="w-[80px] h-[56px] sm:w-[100px] sm:h-[70px] md:w-[140px] md:h-[80px] lg:w-[180px] lg:h-[90px] flex items-center justify-center hover:opacity-80 transition-opacity"
            >
              <img 
                src="/assets/logos/estabrook-logo.svg" 
                alt="Estabrook Logo" 
                className="max-w-full max-h-full" 
              />
            </a>
            <a 
              href="https://modlink.com" 
              target="_blank" 
              rel="noopener noreferrer"
              className="w-[80px] h-[56px] sm:w-[100px] sm:h-[70px] md:w-[140px] md:h-[80px] lg:w-[180px] lg:h-[90px] flex items-center justify-center hover:opacity-80 transition-opacity"
            >
              <img 
                src="/assets/logos/modlink-logo.svg" 
                alt="Modlink Logo" 
                className="max-w-full max-h-full"
              />
            </a>
            <div className="w-[80px] h-[56px] sm:w-[100px] sm:h-[70px] md:w-[140px] md:h-[80px] lg:w-[180px] lg:h-[90px] flex items-center justify-center">
              <img 
                src="/assets/logos/snow-sync-logo.svg" 
                alt="Snow Sync Logo" 
                className="max-w-full max-h-full" 
              />
            </div>
            <div className="w-[80px] h-[56px] sm:w-[100px] sm:h-[70px] md:w-[120px] md:h-[80px] lg:w-[150px] lg:h-[90px] flex items-center justify-center">
              <img 
                src="/assets/logos/asr-logo.svg" 
                alt="ASR Logo" 
                className="max-w-full max-h-full"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Contact Experts Modal */}
      <ContactExpertsModal
        visible={isContactModalVisible}
        onClose={() => setIsContactModalVisible(false)}
        productName={product?.name}
        source="product-detail"
      />
    </div>
  );
};
